import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Button, Pagination, Skeleton } from "@mui/material";
import {
  ArrowBack,
  Update,
  CloudUpload,
  AdminPanelSettings,
  Storage,
  Download,
  Restore,
  Delete,
  Public,
  SettingsBackupRestore,
  Backup,
  Refresh,
  AddCircleOutline,
  PlayArrow,
  Stop,
  ClearAll,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { fetchActivities, setFilters } from "../redux/slices/activitiesSlice";
import { ActivityFilters, EmptyActivities } from "../components/allActivities";

const iconMap = {
  // Site lifecycle
  SITE_CREATED: <AddCircleOutline color="primary" />,
  SITE_UPDATED: <Update color="primary" />,
  SITE_STARTED: <PlayArrow color="success" />,
  SITE_STOPPED: <Stop color="warning" />,
  SITE_RESTARTED: <Refresh color="info" />,
  SITE_DELETED: <Delete color="error" />,
  SITE_SOFT_DELETED: <Delete color="error" />,
  // Backups
  BACKUP_CREATED: <Backup color="success" />,
  BACKUP_DELETED: <SettingsBackupRestore color="error" />,
  // Cache
  CACHE_CLEARED: <ClearAll color="warning" />,
  // Migrations & access
  ENVIRONMENT_MIGRATED: <CloudUpload color="info" />,
  WP_ADMIN_ACCESS: <AdminPanelSettings color="secondary" />,
  PHPMYADMIN_ACCESS: <Storage color="warning" />,
  SITE_DOWNLOADED: <Download color="success" />,
  SITE_RESTORED: <Restore color="success" />,
};

const AllActivities = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    items: activities,
    pagination,
    filters,
    loading,
  } = useSelector((state) => state.activities);

  // Fetch when filters or page change
  useEffect(() => {
    dispatch(fetchActivities({ ...filters, page: pagination.page }));
  }, [dispatch, filters, pagination.page]);

  const handleSearchChange = (e) => {
    dispatch(setFilters({ search: e.target.value }));
  };

  const handleFilterChange = (field) => (e) => {
    dispatch(setFilters({ [field]: e.target.value }));
  };

  const handlePageChange = (_, value) => {
    dispatch(fetchActivities({ ...filters, page: value }));
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          mb: 4,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            All Activities
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View your recent site operations and actions in chronological order.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}
          size="small"
        >
          Back
        </Button>
      </Box>

      {/* Filters */}
      <ActivityFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        iconMap={iconMap}
      />

      {/* Activities List */}
      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={56}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Box>
      ) : activities.length ? (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {activities.map((activity) => {
              const Icon = iconMap[activity.type?.toUpperCase()] || (
                <Public color="action" />
              );
              return (
                <Box
                  key={activity.id}
                  sx={{
                    p: 1.5,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    bgcolor: "background.paper",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 40,
                    }}
                  >
                    {Icon}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{ mb: 0.5 }}
                    >
                      {activity.siteName || "Unknown Site"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {activity.description}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ minWidth: 120, textAlign: "right" }}
                  >
                    {new Date(activity.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Pagination */}
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        </>
      ) : (
        <EmptyActivities />
      )}
    </Box>
  );
};

export default AllActivities;
